// Funnel events (ZIZ-1308). Every push for the e-consult, the results page and the
// checkout goes through here, so the event names and parameters stay the ones in
// docs/tracking/data-dictionary.md.
//
// None of these may carry health information: no question text, answers, block ids or
// entered form values. Products appear only as ecommerce items on the GA4 ecommerce
// events, exactly as the purchase reports them.
import pushToDataLayer from './helpers';
import getGtmProduct from '../helpers/gtmProduct';

const CURRENCY = 'EUR';

// Pushed once per page load (the checkout is one page; its steps are history states).
let beginCheckoutPushed = false;
const formErrorsPushed = new Set();

const toEcommerce = (products) => {
  const items = products.filter(Boolean).map((product, i) => getGtmProduct(product, i));
  const value = items.reduce((sum, item) => sum + item.price, 0);

  return { currency: CURRENCY, value: Number(value.toFixed(2)), items };
};

const pushEcommerce = (event, products, params = {}) => {
  pushToDataLayer({ ecommerce: null });
  pushToDataLayer({ event, ...params, ecommerce: toEcommerce(products) });
};

// Where a deliberate cart change happened: `results` on the advice page, `cart` on the
// checkout's cart step. Speed upgrades pass `upsell` themselves.
export const cartSource = () => (/\/results\/?$/.test(window.location.pathname) ? 'results' : 'cart');

// Sent as `cart_source`, not `source`: GA4 reads an event parameter called `source` as
// the session's traffic source, so a session starting on a cart change was credited to
// "cart" or "upsell" instead of the channel the patient came from.
export const trackAddToCart = (product, source) => {
  pushEcommerce('add_to_cart', [product], { cart_source: source });
};

export const trackRemoveFromCart = (product, source, wasAutoAdded) => {
  pushEcommerce('remove_from_cart', [product], { cart_source: source, was_auto_added: Boolean(wasAutoAdded) });
};

export const trackViewCart = (cart) => {
  pushEcommerce('view_cart', cart);
};

export const trackBeginCheckout = (cart) => {
  if (beginCheckoutPushed) return;
  beginCheckoutPushed = true;
  pushEcommerce('begin_checkout', cart);
};

// Leaving a completed checkout step: 1 → contact details done, 2 → personal details and
// address done (0-based step indexes of the checkout's MultistepForm).
const STEP_COMPLETE_EVENTS = { 1: 'add_contact_info', 2: 'add_shipping_info' };

export const trackStepComplete = (stepIndex, cart) => {
  const event = STEP_COMPLETE_EVENTS[stepIndex];
  if (event) pushEcommerce(event, cart);
};

export const trackAddPaymentInfo = (paymentType, cart) => {
  pushEcommerce('add_payment_info', cart, { payment_type: String(paymentType).toLowerCase() });
};

// Step numbers are 1-based as the patient sees them (?step=N); 0 is the page before the
// checkout (the results page).
export const trackCheckoutBack = (fromStep, toStep) => {
  pushToDataLayer({ event: 'checkout_back', from_step: fromStep, to_step: toStep });
};

// One push per form, field and error type per page load: a patient retrying the same
// invalid postcode is one friction point, not five. `field` must be a fixed key, never a
// label or the value.
export const trackFormError = (form, field, errorType) => {
  const key = `${form}|${field}|${errorType}`;
  if (formErrorsPushed.has(key)) return;
  formErrorsPushed.add(key);
  pushToDataLayer({
    event: 'form_error', form, field, error_type: errorType,
  });
};

// form_error for each listed field a touched vuelidate form holds invalid. `fields` is the
// allow-list of keys that may be reported; the first failing rule becomes the type.
export const trackValidationErrors = (form, validation, fields) => {
  fields.forEach((field) => {
    const fieldValidation = validation[field];
    if (!fieldValidation || !fieldValidation.$invalid) return;

    const failedRule = Object.keys(fieldValidation.$params || {})
      .find((rule) => fieldValidation[rule] === false);
    trackFormError(form, field, failedRule === 'required' ? 'required' : 'invalid');
  });
};

// Class of a failed checkout request: http_4xx, http_5xx, or network (no response).
export const requestErrorType = (error) => {
  const status = error?.response?.status;
  if (!status) return 'network';

  return status >= 500 ? 'http_5xx' : 'http_4xx';
};

export const trackProceedToCheckout = (cart) => {
  pushEcommerce('proceed_to_checkout', cart);
};

export const trackConsultStart = (entryPath) => {
  pushToDataLayer({ event: 'consult_start', entry_path: entryPath });
};

export const trackConsultComplete = (recommendedCount) => {
  pushToDataLayer({
    event: 'consult_complete',
    recommended_count: recommendedCount,
    empty_result: recommendedCount === 0,
  });
};

export const trackConsultAbandon = (stepIndex) => {
  pushToDataLayer({ event: 'consult_abandon', step_index: stepIndex });
};

export const trackCtaClick = (ctaId, destination) => {
  pushToDataLayer({ event: 'cta_click', cta_id: ctaId, destination });
};
